/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import log from "loglevel";
import {getColorIndicator} from "../../../../utils/color-indicator/color-indicator.utils";
import {Manifest, SearchableSortDirection, useSetUserPrefMutation} from "../../../../generated/graphql";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {DragAndDropTargetPayload} from "../../../../providers/AssignmentViewReducer";
import {DispatchRulesDataContext, DispatchRulesDataState} from "../../../../providers/DispatchRulesDataProvider";

import ManifestDriverDetailedCardV2 from "../../common/components/cards/detailed-card/v2/ManifestDriverDetailedCard";
import ManifestDriverMinimumCardV2 from "../../common/components/cards/minimum-card/v2/ManifestDriverMinimumCard";
import ManifestDriverCompactCardV2 from "../../common/components/cards/compact-card/v2/ManifestDriverCompactCard";

import {getHighestRankedColor, StopColor} from "../../common/components/cards/common/components/ManifestCardCommonV2";
import {COLORS, Constants, TenantPreferences, UserPreferences} from "../../../../constants/constants";
import {metersToMiles, millisecondsToDutation} from "../../../../utils/General";
import {DurationIcon, DistanceIcon} from "../../../common/icons";
import {ConditionNames} from "../../../settings/ColorSettings/ColorizedIndicators/RuleGenerator.service";
import {isCompleted} from "../../../../utils/stops/StopUtils";
import {
  createJsonPref,
  extractJsonPref,
  extractSimplePref,
  PreferenceContext
} from "../../../../providers/PreferenceProvider";
import {IManifestCardConfigurationsV2} from "../../card-configuration/v2/types/manifest-card-configurations.type";
import {getDefaultCardConfigurations} from "../../card-configuration/v2/constants/constants";
import {FeatureFlagContext} from "../../../../providers/FeatureFlagProvider";
import {getColorPriority} from "../../../job/JobPanel";
import {CardTypeV2} from "../../card-configuration/v2/constants/enums";
import {useAppContext} from "../../../../ApplicationContext";
import {PendingAssignment} from "../../../../providers/AssignmentReducer";
import {ManifestVisibilityState} from "../../../map/MapVisibilityContext";
import {pendingActionsByManifestDriverId} from "../../../../utils/assignment/PendingAssignment";
import {useAssignmentState} from "../../../../providers/AssignmentProdiver";
import {ManifestDriverMicroCard} from "../../common/components/cards/micro-card/ManifestDriverMicroCard";
import {get, some} from "lodash";
import {useSuggestedAssignmentContext} from "../../../../providers/SuggestedAssignmentProvider";
import {createLayoutEditableFields} from "../../card-configuration/v2/utils/fields-layout-editable.utils";
import {TManifestEnhanced} from "../../../common/types";
import {useManifestDataContext} from "../../providers/ManifestDataProvider";
import {shouldSortManifestsOnClient} from "../utils/colorSort.utils";

const List = styled.span`
  display: flex;
  overflow: hidden;
  height: 100%;
`;

const ScrollContainer = styled.div`
  flex-grow: 1;
  display: flex;
  overflow-y: auto;
  flex-wrap: wrap;
  align-content: flex-start;
  padding: 6px 16px;
  gap: 8px;
`;

const SuggestionCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  padding: 8px;
  background: #ffffff;
  border-radius: 8px;
  font-family: "Roboto", sans-serif;
`;

const SuggestionInfoArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 6px;
`;

const SuggestionTags = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  flex-wrap: wrap;
`;

const SuggestionTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  background: ${COLORS.COLOR_BORDER_LIGHT};
  border-radius: 4px;
  font-size: 12px;
  line-height: 1;
  color: ${COLORS.TEXT_GRAY_MEDIUM};

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const SuggestionReason = styled.div`
  font-size: 12px;
  color: ${COLORS.TEXT_GRAY_MEDIUM};
`;

export type GetSortedByColorManifests = {
  data: Manifest[];
  ruleContextState: DispatchRulesDataState;
  stopColors: any;
  direction: SearchableSortDirection;
};

export const getManifestColors = async ({
  manifest,
  ruleContextState
}: {
  manifest: TManifestEnhanced;
  ruleContextState: DispatchRulesDataState;
}) => {
  const colors: StopColor[] = [];

  //get color of manifest stops
  const stopRules =
    ruleContextState.rules?.filter((rule) => rule.conditions.name !== ConditionNames.MANIFESTONLY) ?? [];
  for (const stop of manifest.stops ?? []) {
    if (!isCompleted(stop)) {
      const engine = ruleContextState.getConfiguredEngine({manifest: manifest, stop: stop}, stopRules);
      if (engine) {
        const color = await getColorIndicator(engine);
        colors.push({color: color, jobStopID: stop.jobStopId});
      }
    }
  }

  //get color if have only manifest status rule {to show in the header of manifest card}
  const onlyManifestRules =
    ruleContextState.rules?.filter((rule) => rule.conditions.name === ConditionNames.MANIFESTONLY) ?? [];
  if (onlyManifestRules.length > 0) {
    const engine = ruleContextState.getConfiguredEngine({manifest: manifest}, onlyManifestRules);
    if (engine) {
      const manifestColor = await getColorIndicator(engine);
      manifestColor && colors.push({color: manifestColor} as StopColor);
    }
  }

  return colors;
};

export const getSortedByColorManifests = ({
  data,
  ruleContextState,
  stopColors,
  direction
}: GetSortedByColorManifests) => {
  const directionValue = direction === SearchableSortDirection.Desc ? -1 : 1;
  const sortedByColorData = [...data].sort((a, b) => {
    const aPriority = getColorPriority(
      ruleContextState.rules,
      getHighestRankedColor(stopColors.get(a.manifestDriverId) ?? [], ruleContextState.colorRank ?? [])
    );
    const bPriority = getColorPriority(
      ruleContextState.rules,
      getHighestRankedColor(stopColors.get(b.manifestDriverId) ?? [], ruleContextState.colorRank ?? [])
    );
    return (aPriority - bPriority) * directionValue;
  });
  return sortedByColorData;
};

const getCardIdV2 = (cardType: CardTypeV2) => {
  switch (cardType) {
    case CardTypeV2.Compact:
      return "manifest-compact-card-item";

    case CardTypeV2.Minimal:
      return "manifest-minimal-card-item";

    case CardTypeV2.Micro:
      return "manifest-micro-card-item";

    default:
      return "manifest-detailed-card-item";
  }
};

const getCardComponentV2 = (cardType: CardTypeV2) => {
  switch (cardType) {
    case CardTypeV2.Compact:
      return ManifestDriverCompactCardV2;

    case CardTypeV2.Minimal:
      return ManifestDriverMinimumCardV2;

    case CardTypeV2.Micro:
      return ManifestDriverMicroCard;

    default:
      return ManifestDriverDetailedCardV2;
  }
};

type ManifestCardListProps = {
  isManifestDetailsDrawerOpen: boolean;
  selectedManifest?: TManifestEnhanced;
  disabledManifestIds: number[];
  data: TManifestEnhanced[];
  sortByColor?: {
    enable: boolean;
    direction: SearchableSortDirection;
  };

  onSelectedManifest(manifest: Manifest, isSelected: boolean): void;
  onDrillDownManifest(manifest: Manifest): void;
  handleUpdateDropTargets(dragAndDropPayload: DragAndDropTargetPayload[]): void;
  pendingActions?: PendingAssignment[];
  visibleManifests: Map<number, ManifestVisibilityState>;
  onLocationMarkerClick(manifest: Manifest): void;
};

const ManifestDriverListV2 = ({
  selectedManifest,
  disabledManifestIds,
  data,
  onSelectedManifest,
  onDrillDownManifest,
  pendingActions,
  handleUpdateDropTargets,
  onLocationMarkerClick,
  sortByColor,
  isManifestDetailsDrawerOpen
}: ManifestCardListProps) => {
  const {
    appState: {isTimeFormat24hr}
  } = useAppContext();
  const {highlightManifests} = useAssignmentState();
  const {isActive: isSuggestionActive, candidates: suggestedCandidates} = useSuggestedAssignmentContext();
  const listRef = useRef<HTMLSpanElement>(null);
  const [stopColors, setStopColors] = useState<any>(new Map<number, StopColor[]>());
  const ruleContextState = useContext<DispatchRulesDataState>(DispatchRulesDataContext);
  const {userPreferences, tenantPreferences, userPrefsQueryRefetch, isUserPreferencesLoaded} =
    useContext(PreferenceContext);
  const {colorSort: colorSortEnable, backendColorRules: backendColorRulesEnabled} = useContext(FeatureFlagContext);
  const {etaLoading} = useManifestDataContext();
  const [setUserPref] = useSetUserPrefMutation();

  const timezoneOfJobStop = useMemo(
    () => extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false).value,
    [tenantPreferences]
  ) as boolean;

  const isUseETAService = useMemo(() => {
    return extractSimplePref(tenantPreferences, TenantPreferences.useEtaService, true).value;
  }, [tenantPreferences]);

  const cardConfigurationsV2: IManifestCardConfigurationsV2 = useMemo(
    () =>
      extractJsonPref(
        userPreferences,
        UserPreferences.manifestCardConfigurations,
        getDefaultCardConfigurations(isUseETAService)
      ).value,
    [userPreferences, isUseETAService]
  );

  const cardTypeV2 = useMemo(() => {
    return cardConfigurationsV2.cardType || CardTypeV2.Compact;
  }, [cardConfigurationsV2.cardType]);

  useEffect(() => {
    if (!isUseETAService && isUserPreferencesLoaded) {
      const currentStopInfo = get(cardConfigurationsV2, "stopInformation", []);
      if (some(currentStopInfo, (info) => info.field === "etaTime")) {
        const updatedStopInfo = currentStopInfo.filter((info) => info.field !== "etaTime");
        const reCalculatedWidthStopInfo = createLayoutEditableFields(
          updatedStopInfo.map((item) => item.field),
          get(cardConfigurationsV2, "cardType", CardTypeV2.Compact)
        );
        setUserPref({
          variables: {
            name: UserPreferences.manifestCardConfigurations,
            input: createJsonPref(
              {
                ...cardConfigurationsV2,
                stopInformation: reCalculatedWidthStopInfo
              },
              true
            )
          }
        })
          .then(() => {
            userPrefsQueryRefetch?.();
          })
          .catch((error: Error) => {
            log.error(error);
          });
      }
    }
  }, [cardConfigurationsV2, isUseETAService, isUserPreferencesLoaded, setUserPref, userPrefsQueryRefetch]);

  useEffect(() => {
    const cardId = getCardIdV2(cardTypeV2);
    const elements = listRef.current?.querySelectorAll(`[data-testid^='${cardId}']`);

    if (elements?.length && elements.length > 0) {
      // Build a lookup map for O(1) access by manifestDriverId
      const manifestMap = new Map(data.map((m) => [m.manifestDriverId, m]));
      const payload: DragAndDropTargetPayload[] = Array.from(elements)
        .map((node) => {
          const cardContainer = (node as HTMLElement).closest("[data-manifestdriverid]") as HTMLElement | null;
          const manifestDriverId = Number(cardContainer?.dataset.manifestdriverid);
          const manifest = manifestMap.get(manifestDriverId);
          if (!manifest) return null;
          return {element: node, manifest} as DragAndDropTargetPayload;
        })
        .filter((item): item is DragAndDropTargetPayload => item !== null);

      handleUpdateDropTargets(payload);
    }
  }, [cardTypeV2, data, handleUpdateDropTargets, isManifestDetailsDrawerOpen]);

  useEffect(() => {
    if (backendColorRulesEnabled) {
      const manifestColors = new Map<number, StopColor[]>();
      for (const manifest of data) {
        const colors: StopColor[] = (manifest.stops ?? [])
          .filter((stop: any) => stop.indicatorColor)
          .map((stop: any) => ({color: stop.indicatorColor, jobStopID: stop.jobStopId}));
        manifestColors.set(manifest.manifestDriverId, colors);
      }
      setStopColors(new Map(manifestColors));
      return;
    }

    // DP-3176: defer client-side colour processing until the initial ETA fetch
    // completes so the engine evaluates with ETA data from the start — avoids a
    // visible colour flicker (no-ETA colours → ETA colours) on panel open.
    if (etaLoading) return;

    let cancelled = false;
    let interval: any;
    const setColors = async () => {
      const manifestColors = new Map<number, StopColor[]>();
      for (const manifest of data) {
        if (cancelled) return;
        const manifestColor = await getManifestColors({manifest, ruleContextState});
        manifestColors.set(manifest.manifestDriverId, manifestColor);
      }
      if (cancelled) return;
      setStopColors(new Map(manifestColors));
    };
    if (!ruleContextState.loading) {
      setColors();
      interval = setInterval(() => {
        setColors();
      }, Constants.GENERAL_POLLING_INTERVAL);
    }
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [ruleContextState, data, backendColorRulesEnabled, etaLoading]);

  const getSortedManifests = useCallback(
    (data: Manifest[]) => {
      if (
        shouldSortManifestsOnClient({
          backendColorRulesEnabled,
          colorSortEnabled: colorSortEnable,
          rulesLoading: ruleContextState.loading,
          sortByColorEnabled: sortByColor?.enable
        })
      ) {
        return getSortedByColorManifests({data, ruleContextState, stopColors, direction: sortByColor!.direction});
      }
      return data;
    },
    [
      backendColorRulesEnabled,
      colorSortEnable,
      ruleContextState,
      sortByColor?.direction,
      sortByColor?.enable,
      stopColors
    ]
  );

  const CardComponentV2 = useMemo(() => getCardComponentV2(cardTypeV2), [cardTypeV2]);

  const cardFunction = useCallback(
    (item: Manifest) => {
      const manifestJobIds = Array.from(new Set([...(item.stops?.map((stop) => stop.job.jobId) ?? [])]));
      const manifestJobStopIds = item.stops?.map((stop) => stop.jobStopId) ?? [];

      const manifestPendingActions = pendingActionsByManifestDriverId(item.manifestDriverId, pendingActions ?? []);

      const pendingJobIds = manifestPendingActions?.flatMap((action) => action.jobIds);
      const pendingJobStopIds = manifestPendingActions?.flatMap((action) => action.jobStopIds);

      const isPending =
        pendingJobIds.some((id) => !manifestJobIds.includes(id)) ||
        pendingJobStopIds.some((id) => !manifestJobStopIds.includes(id));

      const candidate = isSuggestionActive
        ? suggestedCandidates.find((c) => c.manifestDriverId === item.manifestDriverId)
        : undefined;

      const card = (
        <CardComponentV2
          key={`manifest-card-${item.manifestDriverId}`}
          manifest={item}
          colors={stopColors.get(item.manifestDriverId)}
          onSelectedManifest={onSelectedManifest}
          isSelected={item.manifestDriverId === selectedManifest?.manifestDriverId}
          isHighlighted={Boolean(highlightManifests?.includes(item.manifestDriverId))}
          disabled={disabledManifestIds.includes(item.manifestDriverId)}
          isTimeFormat24hr={isTimeFormat24hr}
          timezoneOfJobStop={timezoneOfJobStop}
          isPending={isPending}
          onDrillDownManifest={onDrillDownManifest}
          onLocationMarkerClick={onLocationMarkerClick}
          cardConfigurations={cardConfigurationsV2}
        />
      );

      if (!candidate) return card;

      const duration = candidate.optimizedDuration ? millisecondsToDutation(candidate.optimizedDuration * 1000) : null;
      const distance = candidate.optimizedDistance ? `${metersToMiles(candidate.optimizedDistance)} miles` : null;
      const reasons = candidate.suggestReasons ?? [];
      const hasInfo = duration || distance || reasons.length > 0;

      return (
        <SuggestionCardWrapper key={`suggest-${item.manifestDriverId}`}>
          {hasInfo && (
            <SuggestionInfoArea>
              {(duration || distance) && (
                <SuggestionTags>
                  {duration && (
                    <SuggestionTag>
                      <DurationIcon />
                      {duration}
                    </SuggestionTag>
                  )}
                  {distance && (
                    <SuggestionTag>
                      <DistanceIcon />
                      {distance}
                    </SuggestionTag>
                  )}
                </SuggestionTags>
              )}
              {reasons.map((reason, i) => (
                <SuggestionReason key={i}>{reason}</SuggestionReason>
              ))}
            </SuggestionInfoArea>
          )}
          {card}
        </SuggestionCardWrapper>
      );
    },
    [
      pendingActions,
      CardComponentV2,
      stopColors,
      onSelectedManifest,
      selectedManifest?.manifestDriverId,
      highlightManifests,
      disabledManifestIds,
      isTimeFormat24hr,
      timezoneOfJobStop,
      onDrillDownManifest,
      onLocationMarkerClick,
      cardConfigurationsV2,
      isSuggestionActive,
      suggestedCandidates
    ]
  );

  return (
    <List ref={listRef}>{data && <ScrollContainer>{getSortedManifests(data).map(cardFunction)}</ScrollContainer>}</List>
  );
};

export default ManifestDriverListV2;
